import { bemClassBuilder } from '@vista-digital/bem-class-builder'
import type { Site } from '@vista-digital/ocapi-types/v1'
import {
    addSelectedSite,
    configurationSettings,
    getAllSites,
    getSelectedSites,
    hasMaximumSelectedSites,
    loadAllSites,
    logger,
    nonEmptyStringValidator,
} from '@vista-digital/occ-domain'
import type { SitePickerProps } from '@vista-digital/occ-ui'
import { SitePickerSkeleton, useI18n, useOccConfig } from '@vista-digital/occ-ui'
import { SitePickerEmptyListMessage } from '@vista-digital/occ-ui/src/lib/components/site-picker/subcomponents/empty-list-message'
import {
    SitePickerMessage,
    SitePickerMessageType,
} from '@vista-digital/occ-ui/src/lib/components/site-picker/subcomponents/message'
import { SitePickerUnselectedSiteList } from '@vista-digital/occ-ui/src/lib/components/site-picker/subcomponents/unselected-site-list'
import { sitePickerAssetsValidator } from '@vista-digital/occ-ui/src/lib/components/site-picker/types/assets'
import { sitePickerDisplayTextValidator } from '@vista-digital/occ-ui/src/lib/components/site-picker/types/display-text'
import { defaultSitePickerProps } from '@vista-digital/occ-ui/src/lib/components/site-picker/types/props'
import { withComponentWrapper } from '@vista-digital/occ-ui/src/lib/utils/with-component-wrapper'
import { useMountEffect } from '@vista-digital/react-tools'
import { Validator } from '@vista-digital/validator'
import { observer } from 'mobx-react'
import type { FC } from 'react'
import { useState } from 'react'
import { getFilteredUnselectedSites } from '../utils/getFilteredUnselectedSites'

const b = bemClassBuilder('v-site-picker')

export interface SitePickerPropsWithSiteIds extends SitePickerProps {
    siteIds?: string[]
    onSitesChanged?: () => void
    searchTerm?: string
    filteredSites?: Site[]
}

// eslint-disable-next-line complexity -- todo
export const FilteredSitePicker: FC<SitePickerPropsWithSiteIds> = observer(props => {
    const i18n = useI18n()
    const { enableSkeletonLoadingIndicators } = useOccConfig()

    const allSites = getAllSites()
    const [failedToLoadSites, setFailedToLoadSites] = useState(false)

    const { displayText, assets, siteIds, searchTerm, filteredSites } = props
    useMountEffect(() => {
        void (async () => {
            try {
                await loadAllSites()
            } catch (error) {
                setFailedToLoadSites(true)
                logger.error(`Failed to fetch sites. ${error}`)
            }
        })()
    })

    if (failedToLoadSites) {
        return (
            <SitePickerMessage
                messageType={SitePickerMessageType.FailedToLoadData}
                displayText={displayText}
                assets={assets}
            />
        )
    }

    if (!allSites) {
        return enableSkeletonLoadingIndicators ? <SitePickerSkeleton /> : null
    }

    const handleSitesModified = () => {
        if (props.onSitesChanged) {
            props.onSitesChanged()
        }
    }

    const handleUnselectedSiteClicked = (siteId: string) => {
        addSelectedSite(siteId)
        handleSitesModified()
    }

    const sitesToBeDisplayed = getFilteredUnselectedSites(siteIds, filteredSites, i18n)
    const selectedSites = getSelectedSites()
    const { maximumSelectedSites } = configurationSettings.data.browsing

    const allSitesWithFilter = siteIds?.length ? allSites.filter(s => siteIds.includes(s.id)) : allSites
    const allSitesSelected = selectedSites.length === Math.min(maximumSelectedSites, allSitesWithFilter.length) // allSites.length)

    return (
        <div className={b.block()}>
            {sitesToBeDisplayed.length > 0 ? (
                <SitePickerUnselectedSiteList
                    sites={sitesToBeDisplayed}
                    disabled={hasMaximumSelectedSites()}
                    onSiteClicked={handleUnselectedSiteClicked}
                />
            ) : (
                !allSitesSelected &&
                searchTerm && (
                    <SitePickerEmptyListMessage searchTerm={searchTerm} displayText={displayText} assets={assets} />
                )
            )}
        </div>
    )
})
FilteredSitePicker.displayName = 'FilteredSitePicker'

export const customSitePickerPropsValidator = Validator.object<SitePickerPropsWithSiteIds>({
    siteIds: Validator.optional(Validator.array({ items: nonEmptyStringValidator })),
    onSitesChanged: Validator.optional(Validator.fn()),
    displayText: sitePickerDisplayTextValidator,
    assets: sitePickerAssetsValidator,
    searchTerm: Validator.string(),
    filteredSites: Validator.array(),
})

/**
 * The **Site Picker** component allows the user to select which cinema locations they would like to browse.
 */
export const FilteredSitePickerComponent = withComponentWrapper(
    FilteredSitePicker,
    defaultSitePickerProps,
    customSitePickerPropsValidator
)
